import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../hooks/useAuth';
import { submissionsService } from '../../services/submissions.service';
import LoadingState from '../../components/ui/LoadingState';

const { FiClock, FiCheckCircle, FiXCircle, FiEdit3 } = FiIcons;

export default function VolunteerDashboard() {
  const { contentLanguage } = useTranslation();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      submissionsService.getUserSubmissions()
        .then(data => setSubmissions(data))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  if (!user) {
    return (
      <div className="p-24 text-center text-gray-500">
        <p className="text-xl">{contentLanguage === 'el' ? 'Παρακαλώ συνδεθείτε.' : 'Please sign in.'}</p>
      </div>
    );
  }

  if (loading) return <LoadingState />;

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <SafeIcon icon={FiCheckCircle} className="text-green-500" />;
      case 'rejected': return <SafeIcon icon={FiXCircle} className="text-red-500" />;
      case 'needs_edit': return <SafeIcon icon={FiEdit3} className="text-amber-500" />;
      default: return <SafeIcon icon={FiClock} className="text-blue-500" />;
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-black text-gray-900 mb-8">
        {contentLanguage === 'el' ? 'Πίνακας Ελέγχου Εθελοντή' : 'Volunteer Dashboard'}
      </h1>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {contentLanguage === 'el' ? 'Οι Υποβολές μου' : 'My Submissions'}
          </h2>
        </div>

        {submissions.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            {contentLanguage === 'el' ? 'Δεν υπάρχουν υποβολές.' : 'No submissions yet.'}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {submissions.map(sub => (
              <li key={sub.id} className="p-6 hover:bg-gray-50 transition-colors flex items-center justify-between gap-4">
                <div className="flex-grow">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-400 bg-gray-100 px-2 py-1 rounded">
                      {sub.type}
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(sub.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h3 className="font-bold text-gray-900 text-lg">
                    {sub.content?.title || 'Untitled'}
                  </h3>
                  <p className="text-gray-500 text-sm truncate max-w-xl">
                    {sub.content?.description}
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-white border border-gray-100 rounded-full shadow-sm">
                  {getStatusIcon(sub.status)}
                  <span className="text-sm font-bold capitalize text-gray-700">
                    {sub.status.replace('_', ' ')}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}